import React from "react";
import { useTranslation } from "react-i18next";
import PreviewSafeAnchor from "./PreviewSafeAnchor";

interface BusinessCardFooterProps {
  showFooter: boolean;
  theme?:
    | "luxury"
    | "vintage"
    | "modern"
    | "elegant"
    | "bold"
    | "vibrant"
    | "medical"
    | "tech"
    | "creative"
    | "corporate"
    | "default";
  customContainerClasses?: string;
  customTextClasses?: string;
}

const BusinessCardFooter: React.FC<BusinessCardFooterProps> = ({
  showFooter,
  theme = "default",
  customContainerClasses,
  customTextClasses,
}) => {
  const { t } = useTranslation("clientView");
  if (!showFooter) return null;

  const getThemeStyles = () => {
    switch (theme) {
      case "luxury":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-slate-900 to-black border-t border-amber-500",
          text: "text-amber-200",
          link: "font-bold text-amber-400 hover:text-amber-300 transition-colors font-serif",
        };
      case "vintage":
        return {
          container:
            "px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-gray-100",
          text: "text-gray-500",
          link: "font-semibold text-blue-600 hover:text-blue-700 transition-colors",
        };
      case "modern":
        return {
          container: "px-4 sm:px-6 py-3 bg-white border-t border-gray-100",
          text: "text-gray-500",
          link: "font-semibold text-emerald-600 hover:text-emerald-700 transition-colors",
        };
      case "elegant":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-rose-50 to-pink-50 border-t border-rose-200",
          text: "text-rose-500 font-serif italic",
          link: "font-semibold text-rose-600 hover:text-rose-700 transition-colors font-serif",
        };
      case "bold":
        return {
          container: "px-4 sm:px-6 py-3 bg-black border-t-4 border-yellow-400",
          text: "text-gray-400 font-bold",
          link: "font-black text-yellow-400 hover:text-yellow-300 transition-colors uppercase tracking-wider",
        };
      case "vibrant":
        return {
          container:
            "px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-gray-100",
          text: "text-gray-500",
          link: "font-semibold text-blue-600 hover:text-blue-700 transition-colors",
        };
      case "medical":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 border-t border-teal-200",
          text: "text-teal-600",
          link: "font-semibold text-teal-700 hover:text-teal-800 transition-colors",
        };
      case "tech":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-gray-900 to-black border-t border-green-400",
          text: "text-gray-400 font-mono",
          link: "font-bold text-green-400 hover:text-green-300 transition-colors font-mono",
        };
      case "creative":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border-t border-purple-200",
          text: "text-purple-600",
          link: "font-semibold text-purple-700 hover:text-purple-800 transition-colors",
        };
      case "corporate":
        return {
          container:
            "px-4 sm:px-6 py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-t border-slate-200",
          text: "text-slate-600",
          link: "font-semibold text-slate-700 hover:text-slate-800 transition-colors",
        };
      default:
        return {
          container:
            "px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-gray-100",
          text: "text-gray-500",
          link: "font-semibold text-blue-600 hover:text-blue-700 transition-colors",
        };
    }
  };

  const styles = getThemeStyles();

  return (
    <div className={`w-full ${customContainerClasses || styles.container}`}>
      <p className="text-[10px] text-center" dir="auto">
        <bdi>
          <span className={customTextClasses || styles.text}>
            {t("businessCard.footer.professionalExcellence")}{" "}
          </span>
          <PreviewSafeAnchor
            href="https://super-user-manager.com"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Super User Manager
          </PreviewSafeAnchor>
        </bdi>
      </p>
    </div>
  );
};

export default BusinessCardFooter;
